import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IGNORE_ATTENDEES, IOrganizationsDAO } from '../../../../../api/organizations.dao';
import { RoomConnectionsEntity } from '../../../../../modules/connections/room/room-connections.entity';
import { SEARCH_TERM_MIN_LENGTH } from '../../../../../constants/common.constants';
import { RoomPresentersConnectionsEntity } from '../../../../../modules/connections/room/room-presenters-connections.entity';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { IRoomDetailsDTO, IRoomSaveDTO, IRoomsDAO } from '../../../../../api/rooms.dao';
import { ConnectionType, IConnectionDetailsDTO, IConnectionGroupDTO, IConnectionsDAO, IDeleteConnectionRequestParams } from '../../../../../api/connections.dao';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { alertServerError, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { debounceExecution } from '../../../../../infrastructure/utils/debounce.utils';
import { IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { IUserOperatorDTO } from '../../../../../api/operators.dao';
import { IUsersDAO } from '../../../../../api/users.dao';
import { RoomFormDataVM } from './room-form-data.viewmodel';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import {
    CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS,
    CONNECTION_GROUP_TRACKING_EVENTS,
    convertBoolTypeForMixPanel,
    OpenFrom,
    TRACKING_EVENTS
} from '../../../../../tracking-service/tracking-values';
import { DateFnsFormats, TimeFormats } from '../../../../../infrastructure/date-formatter';
import { dateToHHMM } from '../../../../global/time-input/time-input';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { EventName, IEventDispatcher } from '../../../../../infrastructure/event-dispatcher';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { MixPanelTrackingMeetingConnectionsMapper } from '../../_connections/_meeting-connections-form/mixpanel-tracking-meeting-connections-form.mapper';

type ConnectionTypeOptionList = {id: ConnectionType; name: ConnectionType}[];
class RoomFormVM {
    public static parseSearchQuery(search: string = '', searchMin = SEARCH_TERM_MIN_LENGTH): string {
        const query = search.trim();

        return query.length > searchMin ? query : '';
    }
    static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }
    @mobx.observable
    private roomFormDataVM: RoomFormDataVM;
    @mobx.observable
    private saving: boolean = false;
    @mobx.observable
    private isFormDataReady: boolean = false;
    @mobx.observable
    private connectionsEntity: RoomConnectionsEntity;
    private presenters: IAttendeeOrganizationDTO[] = [];
    @mobx.observable
    private operators: IUserOperatorDTO[] = [];
    private operatorsQuery: string = '';
    private connectionTypeOptionList: ConnectionTypeOptionList = [];
    private readonly eventApiParams: {eventId: number; accountId: number};
    public readonly roomApiParams: {eventId: number; accountId: number; roomId: number};
    @mobx.observable
    private dateFormat: DateFnsFormats;
    private cashedRoomDetails: IRoomDetailsDTO;

    constructor(
        private readonly accountId: number,
        private readonly eventDTO: IEventDetailsDTO,
        private readonly saveCompletedCallback: () => void,
        private readonly roomId: number,
        private openFrom: OpenFrom,
        private readonly deleteCompletionCallback: (roomId: number) => void,
        public readonly handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void,
        public readonly showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void,
        public readonly trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void,
        private readonly roomsDAO: IRoomsDAO = IoC.Container.get(IRoomsDAO),
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly organizationsDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        private readonly dateTimeFormatter: DateTimeFormatter = IoC.Container.get(DateTimeFormatter),
        @IoC.Inject private readonly eventDispatcher: IEventDispatcher = IoC.Container.get(IEventDispatcher)
    ) {
        this.eventApiParams = { eventId: eventDTO.eventId, accountId: accountId };
        this.roomApiParams = { ...this.eventApiParams, roomId: roomId };
        this.dateFormat = this.dateTimeFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours
            ? DateFnsFormats.StringifiedShortFullDateDay24Hours
            : DateFnsFormats.StringifiedShortFullDateDay12Hours;
    }

    public isSaving = (): boolean => this.saving;
    public getIsFormDataReady = (): boolean => this.isFormDataReady;
    public getEvent = (): IEventDetailsDTO => this.globalStorage.getEvent() ?? this.eventDTO;
    public isEditRoom = (): boolean => !!this.roomId;
    public getRoomId = (): null | number => this.roomId;

    public get formDataVM(): RoomFormDataVM { return this.roomFormDataVM; }
    public get presentersVM(): RoomPresentersConnectionsEntity { return this.connectionsEntity.roomPresenters; }
    public get eventPresenters(): IAttendeeOrganizationDTO[] { return this.presenters; }
    public get eventOperators(): IUserOperatorDTO[] { return this.operators; }
    public get connectionTypes(): ConnectionTypeOptionList { return this.connectionTypeOptionList; }
    public get connections(): IConnectionDetailsDTO[] { return this.connectionsEntity.getConnections(); }
    public get connectionGroups(): IConnectionGroupDTO[] { return this.connectionsEntity.getConnectionGroups(); }
    public get defaultConnectionId(): number | null { return this.connectionsEntity.getAssignedConnection(); }
    public get event() { return this.eventApiParams; }
    public get generalRoomId() { return this.roomId; }
    public get getDateFormat() { return this.dateFormat; }

    @mobx.action
    private initConnectionTypes = async (): Promise<void> => {
        try {
            const { types = [] } = await this.connectionsDAO.getConnectionTypes(this.eventApiParams);
            this.connectionTypeOptionList = types.map(ct => ({ id: ct, name: ct }));
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };
    @mobx.action
    private initPresenters = async (): Promise<void> => {
        try {
            const { content } = await this.organizationsDAO.getAllOrganizations({
                ...this.eventApiParams,
                ignoreAttendees: IGNORE_ATTENDEES
            });
            this.presenters = content;
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    @mobx.action
    public async initialize(): Promise<void[]> {
        this.isFormDataReady = false;
        try {
            return Promise.all(
                [ this.initConnectionTypes, this.initPresenters, this.initRoomFormData, this.initOperators ]
                    .map(async fn => await fn())
            );
        } catch (e) {
            RoomFormVM.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isFormDataReady = true;
            });
        }
    }

    @mobx.action
    private initOperators = async (query: string = ''): Promise<void> => {
        try {
            const activeOnlyUsers = { activeOnly: true };
            const { users = [] } = await this.usersDAO.searchDeliveryTeamMembers(query ? {query: query, ...activeOnlyUsers } : { ...activeOnlyUsers } );
            mobx.runInAction(() => {
                this.operators = users.map(user =>
                    ({
                        fullName: `${user.firstName} ${user.lastName}`,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        user: user,
                        active: user?.active !== undefined ? user.active : true,
                    }));
            });
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    @mobx.action
    private async initCreateRoomForm(): Promise<void> {
        this.roomFormDataVM = new RoomFormDataVM(undefined, this.eventDTO);
        this.connectionsEntity = RoomConnectionsEntity.create({roomId: null, connections: [], presenters: []} as IRoomDetailsDTO);
    }
    @mobx.action
    private async initEditRoomForm(): Promise<void> {
        try {
            const roomDetailsDTO = await this.roomsDAO.getRoomById(this.roomApiParams);
            this.cashedRoomDetails = roomDetailsDTO;
            this.roomFormDataVM = new RoomFormDataVM(roomDetailsDTO, this.eventDTO);
            this.connectionsEntity = RoomConnectionsEntity.create(roomDetailsDTO);
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    }

    @mobx.action
    private initRoomFormData = async (): Promise<void> => {
        return this.isEditRoom() ? this.initEditRoomForm() : this.initCreateRoomForm();
    };

    private createRoom = async (): Promise<void> => {
        try {
            const roomDTO = { ...this.roomFormDataVM.toApiDTO(), ...this.connectionsEntity.toDTO() } as IRoomSaveDTO;
            await this.roomsDAO.createRoom(this.roomApiParams.accountId, this.roomApiParams.eventId, roomDTO);
            showNotification('New room added');
            this.trackNewRoomMixPanel(TRACKING_EVENTS.NEW_ROOM, roomDTO);
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    private updateRoom = async (): Promise<void> => {
        try {
            const roomDTO = { ...this.roomFormDataVM.toApiDTO(), ...this.connectionsEntity.toDTO() } as IRoomSaveDTO;
            await this.roomsDAO.updateRoom(this.roomApiParams.accountId, this.roomApiParams.eventId, this.roomApiParams.roomId, roomDTO);
            showNotification('Room details updated');
            this.trackEditRoomMixPanel(TRACKING_EVENTS.EDIT_ROOM, roomDTO);
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    @mobx.action
    public formSubmitAction = async (): Promise<boolean> => {
        if (!this.roomFormDataVM.checkForm() || this.isSaving() || !this.isFormDataReady) return;
        this.saving = true;

        try {
            if (this.isEditRoom()) {
                await this.updateRoom();
            } else {
                await this.createRoom();
            }
            this.saveCompletedCallback();
            return true;
        } catch (error) {
            RoomFormVM.handleApiException(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    private deleteRoom = async resolve => {
        try {
            await this.roomsDAO.deleteRoom(this.roomApiParams);
            this.deleteCompletionCallback(this.roomId);
            this.trackingService.get().track(TRACKING_EVENTS.DELETE_ROOM, {
                'Open From': this.openFrom
            });
            resolve(true);
        } catch (error) {
            resolve(false);
            RoomFormVM.handleApiException(error);
        }
    };

    public confirmDeletingRoom = async () => {
        return await this.promisifiedConfirmDeletingRoom();
    };

    public promisifiedConfirmDeletingRoom = async () => {
        return new Promise<boolean>(resolve => {
            this.alertsService.confirm({
                message: (
                    <span className='alert-message'>
                        You are about to <span className='alert-message--danger'> delete this Room</span>.
                        <br />Are you sure?
                    </span>
                ),
                confirmButtonProps: {
                    handler: () => this.deleteRoom(resolve),
                    text: 'Yes, delete',
                    modifier: 'confirm-warn'
                },
                cancelButtonProps: {
                    modifier: 'cancel'
                }
            });
        });
    };

    public deleteRoomConnection = async (data: IDeleteConnectionRequestParams): Promise<void> => {
        try {
            const {
                meetingConnectionId,
                zoomMeetingDelete = false,
                webinarMeetingDelete = false,
                audioBridgeMeetingDelete = false,
                panelistMeetingDelete = false,
                isPanelistLink = false,
                joinMethodId
            } = data;
            await this.connectionsDAO.deleteRoomConnection({
                meetingConnectionId: meetingConnectionId,
                zoomMeetingDelete: zoomMeetingDelete,
                webinarMeetingDelete: webinarMeetingDelete,
                audioBridgeMeetingDelete: audioBridgeMeetingDelete,
                panelistMeetingDelete: panelistMeetingDelete,
                isPanelistLink: isPanelistLink,
                joinMethodId: joinMethodId,
                ...this.roomApiParams
            });
            mobx.runInAction(() => this.connectionsEntity.removeRoomConnectionById(meetingConnectionId));
            this.eventDispatcher.dispatch(EventName.ConnectionDeleted, { meetingConnectionId: meetingConnectionId }, { roomId: this.roomApiParams.roomId });
        }
        catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    @mobx.action
    public updateDefaultConnection = (connectionId: number | null): void => {
        if (connectionId === null) {
            this.connectionsEntity.removeAssignedConnection();
        } else {
            this.connectionsEntity.assignConnection(connectionId);
        }
    };

    public editRoomConnection = async (connectionDetails: IConnectionDetailsDTO): Promise<void> => {
        try {
            const details = await this.connectionsDAO.editRoomConnection({...connectionDetails, ...this.roomApiParams});
            mobx.runInAction(() => this.connectionsEntity.setRoomConnection(details));
            this.eventDispatcher.dispatch(EventName.ConnectionUpdated, details, { roomId: this.roomApiParams.roomId });
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    public getKnovioRoomConnectionDetails = async (connectionDetails: IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> => {
        return await this.connectionsDAO.getKnovioRoomConnectionDetails({
            ...this.roomApiParams,
            ...{ meetingConnectionId: Number(connectionDetails.meetingConnectionId)
            }
        });
    };

    public createRoomConnection = async (connectionDetails: IConnectionDetailsDTO): Promise<void> => {
        try {
            const details = await this.connectionsDAO.createRoomConnection({...connectionDetails, ...this.roomApiParams});
            mobx.runInAction(() => this.connectionsEntity.setRoomConnection(details));
            this.eventDispatcher.dispatch(EventName.ConnectionCreated, details, { roomId: this.roomApiParams.roomId });
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    public createRoomConnectionGroup = async (connectionGroup: IConnectionGroupDTO): Promise<IConnectionGroupDTO> => {
        try {
            const group = await this.connectionsDAO.createRoomConnectionGroup({ ...connectionGroup, ...this.roomApiParams });
            mobx.runInAction(() => this.connectionsEntity.setRoomConnectionGroup(group));
            this.trackConnectionGroupMixpanelEvent(
                CONNECTION_GROUP_TRACKING_EVENTS.NEW_CONNECTION_GROUP,
                {
                    'Connections': MixPanelTrackingMeetingConnectionsMapper.connectionsTypeByGroup(
                        this.getSortedConnectionList(),
                        connectionGroup
                    )
                }
            );
            return group;
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    public editRoomConnectionGroup = async (connectionGroup: IConnectionGroupDTO): Promise<IConnectionGroupDTO> => {
        try {
            const group = await this.connectionsDAO.editRoomConnectionGroup({
                groupId: connectionGroup.meetingConnectionId,
                ...connectionGroup,
                ...this.roomApiParams
            });
            mobx.runInAction(() => this.connectionsEntity.setRoomConnectionGroup(group));
            this.trackConnectionGroupMixpanelEvent(
                CONNECTION_GROUP_TRACKING_EVENTS.EDIT_CONNECTION_GROUP,
                {
                    'Connection Group ID': connectionGroup.meetingConnectionId,
                    'Connections': MixPanelTrackingMeetingConnectionsMapper.connectionsTypeByGroup(
                        this.getSortedConnectionList(),
                        connectionGroup
                    )
                }
            );
            return group;
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    public deleteRoomConnectionGroup = async (groupId: number): Promise<void> => {
        try {
            await this.connectionsDAO.deleteRoomConnectionGroup({
                groupId: groupId,
                ...this.roomApiParams
            });
            mobx.runInAction(() => this.connectionsEntity.removeRoomConnectionGroupById(groupId));
            this.trackConnectionGroupMixpanelEvent(
                CONNECTION_GROUP_TRACKING_EVENTS.DELETE_CONNECTION_GROUP,
                {
                    'Connection Group ID': groupId,
                }
            );
        } catch (e) {
            RoomFormVM.handleApiException(e);
        }
    };

    public getConnectionGroupList(): IConnectionGroupDTO[] {
        return this.connectionsEntity.getConnectionGroups();
    }

    public getSortedConnectionList(): IConnectionDetailsDTO[] {
        return [...this.connections].sort((a, b) => a.type.localeCompare(b.type));
    }

    @debounceExecution(500)
    public async searchOperators(search: string = ''): Promise<void> {
        const query = RoomFormVM.parseSearchQuery(search);

        if (query === this.operatorsQuery) return;
        await this.initOperators(query);
        this.operatorsQuery = query;
    }

    public selectOperators(operators: IUserOperatorDTO[]): void {
        this.formDataVM.setOperators(operators);
    }


    public getEventDatesArray(): Date[] {
        const res = [];
        const startDate = new Date(this.eventDTO.startDateTime.getTime());
        const endDate = new Date(this.eventDTO.endDateTime.getTime());
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        while (startDate <= endDate) {
            res.push(new Date(startDate.getTime()));
            startDate.setDate(startDate.getDate() + 1);
        }
        return res;
    }

    public trackNewRoomMixPanel = (eventName: string, roomData: IRoomSaveDTO): void => {
        const timeformat = this.dateTimeFormatter.getLocalTimeFormat();

        this.trackingService.get().track(eventName, {
            'Start date': this.dateTimeFormatter.dateToFormattedString(this.roomFormDataVM.startDate, this.dateFormat),
            'End date': this.dateTimeFormatter.dateToFormattedString(this.roomFormDataVM.endDate, this.dateFormat),
            'Start Time': dateToHHMM(roomData.startDateTime, timeformat),
            'End Time': dateToHHMM(roomData.endDateTime, timeformat),
            'Room Label': convertBoolTypeForMixPanel(!!roomData.label),
            'Presenters': convertBoolTypeForMixPanel(roomData.presenters.length > 0),
            'Remove Presenters': 'No',
            'Operators': convertBoolTypeForMixPanel(roomData.addedOperators.length > 0),
            'Remove Operators': 'No',
            'Open From': this.openFrom,
        });
    };

    public trackEditRoomMixPanel = (eventName: string, roomData: IRoomSaveDTO): void => {
        const timeformat = this.dateTimeFormatter.getLocalTimeFormat();
        const isPresentersWereRemoved = this.isPresentersWereRemoved(this.cashedRoomDetails.presenters, roomData.presenters);
        const addOperators = roomData.addedOperators.length > 0;
        const updateOperators = roomData.updatedOperators.length > 0;
        const removeOperators = roomData.removedOperators.length > 0;
        const presentersChanged = isPresentersWereRemoved
            || this.cashedRoomDetails.presenters.length !== roomData.presenters.length
            || !!this.cashedRoomDetails.presenterRoleConnectionId !== !!roomData.presenterRoleConnectionId;

        this.trackingService.get().track(eventName, {
            'Start date': this.dateTimeFormatter.dateToFormattedString(this.roomFormDataVM.startDate, this.dateFormat),
            'End date': this.dateTimeFormatter.dateToFormattedString(this.roomFormDataVM.endDate, this.dateFormat),
            'Start Time': dateToHHMM(roomData.startDateTime, timeformat),
            'End Time': dateToHHMM(roomData.endDateTime, timeformat),
            'Room Label': convertBoolTypeForMixPanel(this.cashedRoomDetails.label !== roomData.label),
            'Presenters': convertBoolTypeForMixPanel(presentersChanged),
            'Remove Presenters': convertBoolTypeForMixPanel(isPresentersWereRemoved),
            'Operators': convertBoolTypeForMixPanel(addOperators || updateOperators || removeOperators),
            'Remove Operators': convertBoolTypeForMixPanel(removeOperators),
            'Host Connection Choose': convertBoolTypeForMixPanel(
                this.cashedRoomDetails.hostRoleConnectionId !== roomData.hostRoleConnectionId),
            'Open From': this.openFrom,
        });
    };

    private isPresentersWereRemoved(
        cashedPresenters: IAttendeeOrganizationDTO[],
        updatedPresenters: IAttendeeOrganizationDTO[]
    ): boolean {

        for (const key of cashedPresenters) {
            if (!updatedPresenters.some(p => p.organizationId === key.organizationId)) {
                return true;
            }
        }
        return false;
    }

    private defaultParamsForMixpanel = (): Record<string, string | number> => {
        return {
            'Account ID': this.globalStorage.getAccount().accountId,
            'Account Name': this.globalStorage.getAccount().name,
            'Event ID': this.globalStorage.getEvent().eventId,
            'Event Name': this.globalStorage.getEvent().name,
            'Room ID': this.roomApiParams.roomId,
        };
    };

    private trackConnectionGroupMixpanelEvent = (eventName: string, props: Record<string, string | number>): void => {
        this.trackingService.get().track(eventName, {
            ...this.defaultParamsForMixpanel(),
            ...props
        });
    };
}

export { RoomFormVM };
